<script lang="ts" setup>
  import type { AspectRatio, ImgLoading } from '@/types/media'
  import type { TypePanelHeroComponent } from '@/types/contentful'

  const props = withDefaults(
    defineProps<{
      data: TypePanelHeroComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>
      loading?: ImgLoading
      preload?: boolean
    }>(),
    {
      loading: 'lazy',
      preload: false
    }
  )

  const desktopRatios: Record<'L' | 'M' | 'S', AspectRatio> = {
    S: { numerator: 4, denominator: 1 },
    M: { numerator: 3, denominator: 1 },
    L: { numerator: 2, denominator: 1 }
  }

  const desktopAspectRatio = computed<AspectRatio>(() => desktopRatios[props.data.fields.size])
  const hasButtons = computed<boolean>(() => !!props.data.fields.primaryButton || !!props.data.fields.secondaryButton)
  const isDarkMode = computed<boolean>(() => props.data.fields.base?.fields.theme === 'Dark')
  const alignment = computed(() => props.data.fields.text?.fields.alignment)
</script>

<template>
  <section class="m-panel-hero">
    <div
      :style="{
        '--aspect-ratio-desktop': `${desktopAspectRatio.numerator} / ${desktopAspectRatio.denominator}`
      }"
      class="hero-container"
    >
      <div class="hero-media">
        <CImageBlock
          v-if="data.fields.desktopImage?.fields.file"
          :alt="data.fields.desktopImage.fields.description"
          :loading
          :preload
          :src="data.fields.desktopImage.fields.file.url"
          class="hero-image hero-image-desktop"
        />
        <CImageBlock
          v-if="data.fields.mobileImage?.fields.file"
          :alt="data.fields.mobileImage.fields.description"
          :loading
          :preload
          :src="data.fields.mobileImage.fields.file.url"
          class="hero-image hero-image-mobile"
        />
      </div>

      <div
        :class="[
          'hero-content',
          {
            'prop-align-left': alignment === 'Left',
            'prop-align-center': alignment === 'Centre',
            'prop-align-right': alignment === 'Right'
          }
        ]"
      >
        <div class="content-panel">
          <CTextBlock
            :dark-mode="isDarkMode"
            :link-button="data.fields.linkButton"
            :text="data.fields.text"
            overflow-wrap="normal"
          />

          <CButtonBlock
            v-if="hasButtons"
            :dark="isDarkMode"
            :primary-button="data.fields.primaryButton"
            :secondary-button="data.fields.secondaryButton"
            class="button-block"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
  $overlay-breakpoint: 1024px;

  .m-panel-hero {
    width: 100%;
    background-color: v-bind('data.fields.base?.fields.backgroundColour');
  }

  .hero-container {
    width: 100%;
    max-width: var(--breakpoint-xl);
    margin: 0 auto;
    background-color: v-bind('data.fields.backgroundColour');

    @media (min-width: $overlay-breakpoint) {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      position: relative;
      min-height: 360px;

      &::before {
        content: '';
        grid-area: 1 / 1;
        min-width: 0;
        width: 100%;
        aspect-ratio: var(--aspect-ratio-desktop);
      }
    }
  }

  .hero-media {
    width: 100%;
    height: 270px;
    overflow: hidden;

    @media (min-width: $overlay-breakpoint) {
      position: relative;
      grid-area: 1 / 1;
      min-width: 0;
      height: auto;
    }
  }

  .hero-image {
    width: 100%;
    height: 100%;

    :deep(.graphic) {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @media (min-width: $overlay-breakpoint) {
      position: absolute;
      inset: 0;
    }
  }

  .hero-image-desktop {
    display: none;

    @media (min-width: $overlay-breakpoint) {
      display: block;
    }
  }

  .hero-image-mobile {
    display: block;

    @media (min-width: $overlay-breakpoint) {
      display: none;
    }
  }

  .hero-content {
    display: flex;
    width: 100%;
    background-color: v-bind('data.fields.backgroundColour');

    @media (min-width: $overlay-breakpoint) {
      position: relative;
      z-index: 1;
      grid-area: 1 / 1;
      min-width: 0;
      width: auto;
      align-items: center;
      padding: 48px clamp(40px, 4.65vw, 68px);
      background-color: transparent;
      pointer-events: none;
    }
  }

  .content-panel {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    padding: 28px clamp(16px, 4vw, 40px);
    background-color: v-bind('data.fields.backgroundColour');

    @media (min-width: $overlay-breakpoint) {
      width: min(520px, 100%);
      padding: 40px;
      border-radius: 24px;
      pointer-events: auto;
    }
  }

  .button-block {
    flex-wrap: wrap;
  }

  .hero-content.prop-align-left {
    justify-content: flex-start;

    .button-block {
      justify-content: flex-start;
    }
  }

  .hero-content.prop-align-center {
    justify-content: center;

    .button-block {
      justify-content: center;
    }
  }

  .hero-content.prop-align-right {
    justify-content: flex-end;

    .button-block {
      justify-content: flex-end;
    }
  }
</style>
